import {
  CREDIT_CARD_SUBTYPE_KEYS,
  DEPOSITORY_SUBTYPE_KEYS,
  SUPPORTED_FINANCIAL_ACCOUNT_SUBTYPE_KEYS,
} from '@orn/global/financialAccounts/supportedAccountTypes.js';

import { FinancialAccountPrimaryType, FinancialAccountSubtype } from '../__generated__/globalTypes';

import { firstLetterCapitalize } from './formatting';

import type { FinancialAccount } from '../hooks/queries/useFinancialAccounts';

interface IAccountTypeCheck {
  type: FinancialAccountPrimaryType;
  subtype: FinancialAccountSubtype;
}

// type-graphql exposes the core enum's key names over the wire, so this app's
// codegen'd `FinancialAccountSubtype` *values* equal the @orn/global key strings
// (`Checking = 'CHECKING'`). Resolve the shared keys to enum values via lookup —
// no cast; the sibling unit test pins the invariant so a codegen change can't
// silently empty the supported set.
function toSubtype(key: string): FinancialAccountSubtype | undefined {
  return Object.values(FinancialAccountSubtype).find((value) => String(value) === key);
}

function toSubtypeSet(keys: readonly string[]): ReadonlySet<FinancialAccountSubtype> {
  return new Set(keys.map(toSubtype).filter((value): value is FinancialAccountSubtype => value !== undefined));
}

const DEPOSITORY_SUBTYPES = toSubtypeSet(DEPOSITORY_SUBTYPE_KEYS);
const CREDIT_CARD_SUBTYPES = toSubtypeSet(CREDIT_CARD_SUBTYPE_KEYS);

export function isDepositoryAccount(account: IAccountTypeCheck): boolean {
  return account.type === FinancialAccountPrimaryType.Depository && DEPOSITORY_SUBTYPES.has(account.subtype);
}

export function isCreditCardAccount(account: IAccountTypeCheck): boolean {
  return account.type === FinancialAccountPrimaryType.Credit && CREDIT_CARD_SUBTYPES.has(account.subtype);
}

export function isSupportedFinancialAccount(account: IAccountTypeCheck): boolean {
  return isDepositoryAccount(account) || isCreditCardAccount(account);
}

/** The per-account notification threshold an account type supports, if any. */
export type ThresholdKind = 'lowBalance' | 'creditUtilization';

/**
 * Which per-account alert threshold applies to an account: a depository account
 * has a low-balance ($) threshold, a credit card a utilization (%) threshold,
 * and every other type none.
 */
export function getThresholdKind(account: IAccountTypeCheck): ThresholdKind | null {
  if (isDepositoryAccount(account)) {
    return 'lowBalance';
  }
  if (isCreditCardAccount(account)) {
    return 'creditUtilization';
  }
  return null;
}

export const supportedSubtypes: FinancialAccountSubtype[] = SUPPORTED_FINANCIAL_ACCOUNT_SUBTYPE_KEYS.map(
  toSubtype
).filter((value): value is FinancialAccountSubtype => value !== undefined);

/**
 * Returns a plural display name for account subtypes.
 * Used for section headers (e.g., "Credit cards").
 */
export function subtypeToDisplayName(subtype: FinancialAccountSubtype): string {
  switch (subtype) {
    case FinancialAccountSubtype.Checking:
      return 'Checking';
    case FinancialAccountSubtype.Savings:
      return 'Savings';
    case FinancialAccountSubtype.CreditCard:
      return 'Credit cards';
    default:
      return 'Unrecognized';
  }
}

/**
 * Returns a singular display name for account subtypes.
 * Used for individual account display (e.g., "Credit Card").
 */
export const formatAccountSubtype = (subtype: FinancialAccountSubtype): string => {
  const subtypeMap: Partial<Record<FinancialAccountSubtype, string>> = {
    [FinancialAccountSubtype.Checking]: 'Checking',
    [FinancialAccountSubtype.Savings]: 'Savings',
    [FinancialAccountSubtype.CreditCard]: 'Credit Card',
    [FinancialAccountSubtype.MoneyMarket]: 'Money Market',
    [FinancialAccountSubtype.Cd]: 'CD',
    [FinancialAccountSubtype.Brokerage]: 'Brokerage',
    [FinancialAccountSubtype.Ira]: 'IRA',
    [FinancialAccountSubtype.Roth]: 'Roth',
    [FinancialAccountSubtype.Roth_401K]: 'Roth 401K',
    [FinancialAccountSubtype.Plan_401K]: '401K',
    [FinancialAccountSubtype.Plan_403B]: '403B',
    [FinancialAccountSubtype.Plan_529]: '529 Plan',
    [FinancialAccountSubtype.Loan]: 'Loan',
    [FinancialAccountSubtype.Mortgage]: 'Mortgage',
    [FinancialAccountSubtype.Auto]: 'Auto Loan',
    [FinancialAccountSubtype.Student]: 'Student Loan',
    [FinancialAccountSubtype.HomeEquity]: 'Home Equity Loan',
    [FinancialAccountSubtype.LineOfCredit]: 'Line of Credit',
    [FinancialAccountSubtype.Paypal]: 'PayPal',
    [FinancialAccountSubtype.Prepaid]: 'Prepaid',
    [FinancialAccountSubtype.Other]: 'Other Account',
  };

  return subtypeMap[subtype] || firstLetterCapitalize(subtype.replace(/_/g, ' '));
};

export function groupAccountsBySubtype(
  financialAccounts: FinancialAccount[]
): Record<FinancialAccountSubtype, FinancialAccount[]> {
  const grouped = financialAccounts.reduce(
    (acc, account) => {
      const subtype = account.subtype;
      if (!acc[subtype]) {
        acc[subtype] = [];
      }
      acc[subtype].push(account);
      return acc;
    },
    {} as Record<FinancialAccountSubtype, FinancialAccount[]>
  );

  // Sort accounts within each subtype by bank name, then account name
  for (const subtype of Object.keys(grouped) as FinancialAccountSubtype[]) {
    grouped[subtype].sort((a, b) => {
      const bankCompare = a.bank.name.localeCompare(b.bank.name);
      if (bankCompare !== 0) {
        return bankCompare;
      }
      return a.name.localeCompare(b.name);
    });
  }

  return grouped;
}
